/**
 * Accordion Search Functionality
 * Provides search and highlighting for accordion blocks
 *
 * initAccordionSearch(selector):
 * - Single accordion: selector is `.accordion--{id}` — searches rows inside that block only.
 * - FAQ page: selector is `[data-faq-search-root]` — search input lives here; accordions are resolved
 *   under the closest `[data-faq-section-root]`, else `.main-page`, else `.shopify-section`
 *   (prefer `.accordion--faq_page`).
 */
(function() {
  'use strict';

  /**
   * @param {string} selector - Root element (accordion container or `[data-faq-search-root]`)
   */
  function initAccordionSearch(selector) {
    const root = document.querySelector(selector);
    if (!root) return;

    const isFaqPageRoot = root.hasAttribute('data-faq-search-root');
    const searchInput = root.querySelector('.accordion__search-input');
    const clearButton = root.querySelector('.accordion__search-clear');

    /** @type {NodeListOf<HTMLElement>} */
    let accordionItems;

    if (isFaqPageRoot) {
      const scope =
        root.closest('[data-faq-section-root]') ||
        root.closest('.main-page') ||
        root.closest('.shopify-section');
      if (!scope) return;
      const scoped = scope.querySelectorAll('.accordion--faq_page accordion-custom');
      accordionItems =
        scoped.length > 0
          ? scoped
          : scope.querySelectorAll('.accordion__items accordion-custom');
    } else {
      accordionItems = root.querySelectorAll('.accordion__items accordion-custom');
    }

    if (!searchInput || !clearButton || !accordionItems.length) return;

    /**
     * Gets searchable text from an accordion item
     * @param {HTMLElement} accordionItem - The accordion item element
     * @returns {string} Lowercase searchable text
     */
    function getSearchableText(accordionItem) {
      const titleHeading = accordionItem.querySelector('.accordion-row__title');
      const summary = accordionItem.querySelector('summary');
      const content = accordionItem.querySelector('.details-content');

      let text = '';
      if (titleHeading) {
        const titleClone = /** @type {HTMLElement} */ (titleHeading.cloneNode(true));
        const titleIcons = titleClone.querySelectorAll('.details__icon');
        titleIcons.forEach(/** @type {HTMLElement} */ (icon) => icon.remove());
        text += (titleClone.textContent || '').trim() + ' ';
      }
      if (summary) {
        const summaryClone = /** @type {HTMLElement} */ (summary.cloneNode(true));
        const icons = summaryClone.querySelectorAll('.svg-wrapper, .details__icon');
        icons.forEach(/** @type {HTMLElement} */ (icon) => icon.remove());
        const summaryText = summaryClone.textContent || '';
        text += summaryText.trim() + ' ';
      }
      if (content) {
        const contentText = content.textContent || '';
        text += contentText.trim();
      }
      return text.toLowerCase();
    }

    /**
     * Removes all highlight marks from an element
     * @param {HTMLElement} element - The element to remove highlights from
     */
    function removeHighlights(element) {
      const highlights = element.querySelectorAll('.accordion__search-highlight');
      highlights.forEach(/** @type {HTMLElement} */ (highlight) => {
        const parent = highlight.parentNode;
        if (parent) {
          parent.replaceChild(document.createTextNode(highlight.textContent), highlight);
          parent.normalize();
        }
      });
    }

    /**
     * Highlights matching text in an element
     * @param {HTMLElement} element - The element to highlight text in
     * @param {string} searchTerm - The search term to highlight
     */
    function highlightText(element, searchTerm) {
      if (!searchTerm || searchTerm.trim() === '') {
        removeHighlights(element);
        return;
      }

      const term = searchTerm.trim();
      const walker = document.createTreeWalker(
        element,
        NodeFilter.SHOW_TEXT,
        {
          acceptNode: function(node) {
            let parent = node.parentElement;
            while (parent && parent !== element) {
              if (
                parent.classList.contains('svg-wrapper') ||
                parent.classList.contains('details__icon') ||
                parent.classList.contains('accordion__search-highlight')
              ) {
                return NodeFilter.FILTER_REJECT;
              }
              parent = parent.parentElement;
            }
            return NodeFilter.FILTER_ACCEPT;
          }
        }
      );

      const textNodes = [];
      let node;
      while ((node = walker.nextNode())) {
        textNodes.push(node);
      }

      textNodes.forEach((textNode) => {
        const text = textNode.textContent;
        if (!text) return;

        const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`(${escapedTerm})`, 'gi');

        if (regex.test(text)) {
          const highlightedText = text.replace(
            regex,
            '<mark class="accordion__search-highlight">$1</mark>'
          );
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = highlightedText;

          const parent = textNode.parentNode;
          if (!parent) return;

          const fragment = document.createDocumentFragment();

          while (tempDiv.firstChild) {
            fragment.appendChild(tempDiv.firstChild);
          }

          parent.replaceChild(fragment, textNode);
        }
      });
    }

    /**
     * Filters accordion items based on search term
     * @param {string} searchTerm - The search term to filter by
     * @returns {boolean} Whether any results were found
     */
    function filterAccordionItems(searchTerm) {
      const term = searchTerm.toLowerCase().trim();
      const minSearchLength = 3;
      let hasResults = false;

      const shouldFilter = term === '' || term.length >= minSearchLength;

      accordionItems.forEach((item) => {
        const titleEl = item.querySelector('.accordion-row__title');
        const summaryEl = item.querySelector('summary');
        const contentEl = item.querySelector('.details-content');
        const title = titleEl ? /** @type {HTMLElement} */ (titleEl) : null;
        const summary = summaryEl ? /** @type {HTMLElement} */ (summaryEl) : null;
        const content = contentEl ? /** @type {HTMLElement} */ (contentEl) : null;
        const details = item.querySelector('details');

        if (title) removeHighlights(title);
        if (summary) removeHighlights(summary);
        if (content) removeHighlights(content);

        if (!shouldFilter) {
          item.removeAttribute('data-hidden');
          hasResults = true;
          return;
        }

        const searchableText = getSearchableText(/** @type {HTMLElement} */ (item));
        const matches = term === '' || searchableText.includes(term);

        if (matches) {
          item.removeAttribute('data-hidden');

          if (term !== '') {
            if (title) highlightText(title, searchTerm);
            if (summary) highlightText(summary, searchTerm);
            if (content) highlightText(content, searchTerm);

            if (details && !details.open) {
              details.open = true;
            }
          } else {
            if (details && details.open) {
              details.open = false;
            }
          }

          hasResults = true;
        } else {
          item.setAttribute('data-hidden', 'true');
        }
      });

      return hasResults;
    }

    function updateClearButton() {
      const input = /** @type {HTMLInputElement} */ (searchInput);
      if (input && input.value.trim() !== '') {
        if (clearButton) {
          clearButton.classList.remove('visually-hidden');
        }
      } else if (clearButton) {
        clearButton.classList.add('visually-hidden');
      }
    }

    if (searchInput) {
      const input = /** @type {HTMLInputElement} */ (searchInput);
      input.addEventListener('input', (e) => {
        const target = /** @type {HTMLInputElement} */ (e.target);
        filterAccordionItems(target.value);
        updateClearButton();
      });
    }

    if (clearButton) {
      clearButton.addEventListener('click', () => {
        const input = /** @type {HTMLInputElement} */ (searchInput);
        if (input) {
          input.value = '';
          filterAccordionItems('');
          updateClearButton();
          input.focus();
        }
      });
    }

    updateClearButton();
  }

  // @ts-ignore
  window.initAccordionSearch = initAccordionSearch;
})();
